import React, { useEffect, useState } from "react";
import {
  AlertProps,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Container,
  Alert,
  useTheme,
  useMediaQuery,
  Fade,
  Divider,
  Stack,
  Paper,
  Chip,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import {
  Visibility,
  VisibilityOff,
  PersonAdd,
  ArrowBack,
  CheckCircle,
  Error,
  Info,
  Security,
} from "@mui/icons-material";
import Logo from "../../EasyAgility.png";
import axios from "axios";
import Cookies from "universal-cookie";
import { add } from "date-fns";
import PasswordStrengthBar from "react-password-strength-bar";
import Notify from "../Notify/component";
import CreateClubAuthPrompt from "../AgilityClubs/CreateClubAuthPrompt";
import {
  createClubRedirectPath,
  hasCreateClubIntent,
} from "../../utils/createClub";
import type { jwtCookie } from "../Login/component";

const Register = ({
  setRegister,
  setLoggedIn,
}: {
  setRegister: (register: boolean) => void;
  setLoggedIn?: (loggedIn: boolean) => void;
}): JSX.Element => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [score, setScore] = useState(0);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertProps["severity"]>("error");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const validateEmail = (email: string): RegExpMatchArray | null => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const getPasswordStrengthLabel = (score: number) => {
    if (score >= 4) return "Very Strong";
    if (score >= 3) return "Strong";
    if (score >= 2) return "Good";
    if (score >= 1) return "Weak";
    return "Very Weak";
  };

  const isFormValid = () => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.length > 0 &&
      password.length > 0 &&
      confirmPassword.length > 0 &&
      validateEmail(email) &&
      score >= 2 &&
      password === confirmPassword
    );
  };

  const handleRegister = async () => {
    if (!isFormValid()) {
      setMessage(
        "Please check all fields and ensure they meet the requirements",
      );
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `https://identity.easyagility.co.uk/register`,
        {
          email_address: email,
          password: password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
        {
          headers: {
            Authorization: "Basic Mzc1OTozNzQ4MjEwMjM=",
          },
        },
      );

      if (hasCreateClubIntent(window.location.search)) {
        const loginResponse = await axios.post(
          `https://identity.easyagility.co.uk/token?scope=all&username=${email}&password=${encodeURIComponent(
            password,
          )}&grant_type=password`,
          {},
          {
            headers: {
              Authorization: "Basic Mzc1OTozNzQ4MjEwMjM=",
            },
          },
        );
        const expiryTime: Date = add(new Date(), {
          seconds: loginResponse.data.expires_in,
        });
        const cookieOptions: jwtCookie = {
          expires: expiryTime,
          path: "/",
          sameSite: "strict" as "strict",
          secure:
            window.location.origin.indexOf("easyagility") !== -1 || undefined,
        };
        const cookies = new Cookies();
        cookies.set("token", loginResponse.data.access_token, cookieOptions);
        setLoggedIn?.(true);
        window.location.href = createClubRedirectPath();
        return;
      }

      setRegister(false);
      setMessage(
        "You have been registered successfully! You can now log in with your new account.",
      );
      setSeverity("success");
      setNotifyOpen(true);
    } catch (err) {
      setMessage(
        "There was an error registering you, please try again or contact matt@easyagility.co.uk if the problem persists",
      );
      setSeverity("error");
      setNotifyOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        handleRegister();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
    // eslint-disable-next-line
  }, [email, firstName, lastName, password, confirmPassword]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Grid container spacing={4} alignItems="center">
          {/* Left Side - Logo and Info */}
          <Grid item xs={12} md={5}>
            <Fade in timeout={600}>
              <Box sx={{ textAlign: "center", mb: { xs: 4, md: 0 } }}>
                <img
                  src={Logo}
                  alt="EasyAgility Logo"
                  style={{
                    height: isMobile ? "150px" : "200px",
                    width: "auto",
                    maxWidth: "100%",
                    marginBottom: theme.spacing(3),
                  }}
                />
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 2,
                    fontSize: { xs: "1.8rem", md: "2rem" },
                  }}
                >
                  {hasCreateClubIntent(window.location.search)
                    ? "Create your account"
                    : "Join Easy Agility Hub"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    maxWidth: 400,
                    mx: "auto",
                  }}
                >
                  {hasCreateClubIntent(window.location.search)
                    ? "Create an account to set up your agility club in sandbox mode."
                    : "Create your account to start managing your agility show entries and tracking your dogs' progress."}
                </Typography>
              </Box>
            </Fade>
          </Grid>

          {/* Right Side - Registration Form */}
          <Grid item xs={12} md={7}>
            <Fade in timeout={800}>
              <Paper
                elevation={8}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <IconButton
                    onClick={() => setRegister(false)}
                    sx={{
                      mr: 2,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light + "20",
                      },
                    }}
                    aria-label="Go back to login"
                  >
                    <ArrowBack />
                  </IconButton>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {hasCreateClubIntent(window.location.search)
                      ? "Create account to set up your club"
                      : "Create Account"}
                  </Typography>
                </Box>

                {hasCreateClubIntent(window.location.search) && (
                  <CreateClubAuthPrompt />
                )}

                <Stack spacing={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        label="First name"
                        variant="outlined"
                        autoComplete="given-name"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        label="Last name"
                        variant="outlined"
                        autoComplete="family-name"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Email Field */}
                  <TextField
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    label="Email Address"
                    variant="outlined"
                    type="email"
                    autoComplete="email"
                    error={email.length > 0 && !validateEmail(email)}
                    helperText={
                      email.length > 0 && !validateEmail(email)
                        ? "Please enter a valid email address"
                        : ""
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Password Field */}
                  <Box>
                    <TextField
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      label="Password"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <PasswordStrengthBar
                        onChangeScore={(score) => setScore(score)}
                        password={password}
                        scoreWords={[
                          "Very Weak",
                          "Weak",
                          "Good",
                          "Strong",
                          "Very Strong",
                        ]}
                        shortScoreWord="Too Short"
                        barColors={[
                          theme.palette.error.main,
                          theme.palette.warning.main,
                          theme.palette.info.main,
                          theme.palette.success.main,
                          theme.palette.success.dark,
                        ]}
                      />
                      {password.length > 0 && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", mt: 1 }}
                        >
                          <Chip
                            label={getPasswordStrengthLabel(score)}
                            size="small"
                            color={
                              score >= 4
                                ? "success"
                                : score >= 3
                                  ? "warning"
                                  : score >= 2
                                    ? "info"
                                    : "error"
                            }
                            icon={score >= 2 ? <CheckCircle /> : <Error />}
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {score >= 2
                              ? "Password meets requirements"
                              : "Password needs to be at least 'Good' strength"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Confirm Password Field */}
                  <TextField
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    label="Confirm Password"
                    variant="outlined"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    error={
                      confirmPassword.length > 0 && password !== confirmPassword
                    }
                    helperText={
                      confirmPassword.length > 0 && password !== confirmPassword
                        ? "Passwords do not match"
                        : ""
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            aria-label="toggle confirm password visibility"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Password Requirements */}
                  <Alert
                    severity="info"
                    icon={<Security />}
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-message": {
                        fontSize: { xs: "0.8rem", md: "0.9rem" },
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Password Requirements:
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        • Valid email address
                      </Typography>
                      <Typography variant="body2">
                        • Password strength must be at least "Good"
                      </Typography>
                      <Typography variant="body2">
                        • Minimum 6 characters with letters, numbers, and
                        special characters
                      </Typography>
                      <Typography variant="body2">
                        • Passwords must match
                      </Typography>
                    </Stack>
                  </Alert>

                  {/* Register Button */}
                  <Button
                    variant="contained"
                    onClick={handleRegister}
                    disabled={!isFormValid() || loading}
                    startIcon={<PersonAdd />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {loading ? "Creating Account..." : "Create Account"}
                  </Button>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      or
                    </Typography>
                  </Divider>

                  {/* Back to Login */}
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => setRegister(false)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1.5,
                    }}
                  >
                    Already have an account? Sign In
                  </Button>
                </Stack>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {/* Help Section */}
        <Fade in timeout={1200}>
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Alert
              severity="info"
              icon={<Info />}
              sx={{
                maxWidth: 600,
                mx: "auto",
                borderRadius: 2,
                "& .MuiAlert-message": {
                  fontSize: { xs: "0.9rem", md: "1rem" },
                },
              }}
            >
              Need help with registration? Contact us at{" "}
              <strong>matt@easyagility.co.uk</strong> for support.
            </Alert>
          </Box>
        </Fade>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/privacy-policy" underline="hover">
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Container>

      <Notify
        open={notifyOpen}
        setOpen={setNotifyOpen}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default Register;
